import React, { FC, useCallback } from 'react';
import Slider from 'react-slick';
import { Box, Flex } from '@withjoy/joykit';
import { backgroundImageForUrl } from '@shared/utils/style/backgroundImage';
import { addRendition } from '@shared/utils/photoRendition';
import { useGetFilledPhotos } from '../../hooks/useGetFilledPhotos';
import { SliderContainer } from './PhotoSlider.styles';
import { Photo, AlohaPhotoSliderProps } from './PhotoSlider.types';
import { CroppedImage } from '@shared/components/MultiPhotoSelector/components/CroppedImage/CroppedImage';
import { useGetContainerDimensions } from '../../hooks/useGetContainerDimensions';
import { useSliderSettings } from './hooks/useSliderSettings';
import { PHOTO_COUNT_TO_SHOW, ASPECT_RATIO } from './PhotoSlider.constants';

export const AlohaPhotoSlider: FC<AlohaPhotoSliderProps> = props => {
  const { photos, photoHeight, showPlaceholder } = props;

  const { filledPhotos } = useGetFilledPhotos(photos, PHOTO_COUNT_TO_SHOW, showPlaceholder && photos.length === 0);

  const { containerDimensions, containerRef } = useGetContainerDimensions();
  const { settings, sliderRef } = useSliderSettings();

  const getPhotoComponent = useCallback(
    (photo: Photo) => {
      if (photo.cropInfo) {
        return (
          <Box
            key={photo.id}
            width="100%"
            height="100%"
          >
            <Flex
              width="100%"
              height={photoHeight || '100%'}
              display="flex"
              justifyContent="center"
              alignItems="center"
            >
              <CroppedImage
                width={Math.max(containerDimensions.width, containerDimensions.height)}
                height={Math.max(containerDimensions.width, containerDimensions.height)}
                key={photo.id}
                imageUrl={addRendition({ url: photo.photoUrl, renditionSize: 'medium' })}
                cropInfo={{ ...photo.cropInfo, unit: '%' }}
                canvasStyles={{ position: 'absolute' }}
              />
            </Flex>
          </Box>
        );
      } else {
        return (
          <Box
            key={photo.id}
            width="100%"
            height={photoHeight || '100%'}
            aspectRatio={{ _: ASPECT_RATIO.mobile, sm2: ASPECT_RATIO.desktop }}
            backgroundImage={backgroundImageForUrl(addRendition({ url: photo.photoUrl, renditionSize: 'medium' }))}
            backgroundSize="cover"
            backgroundRepeat="no-repeat"
            backgroundPosition="center"
          />
        );
      }
    },
    [containerDimensions.height, containerDimensions.width, photoHeight],
  );

  return (
    <SliderContainer
      ref={containerRef}
      width="100%"
      height="100%"
      position="relative"
    >
      <Slider
        ref={sliderRef}
        {...settings}
      >
        {filledPhotos.map((photo, index) => {
          return photo.photoUrl ? (
            getPhotoComponent(photo)
          ) : (
            <Box
              key={index}
              width="100%"
              height={photoHeight || '100%'}
              aspectRatio={{ _: ASPECT_RATIO.mobile, sm2: ASPECT_RATIO.desktop }}
              backgroundColor="lightgray"
            />
          );
        })}
      </Slider>
    </SliderContainer>
  );
};

AlohaPhotoSlider.displayName = 'AlohaPhotoSlider';
